import { useState } from 'react'
import type { Preco, Produto, Representada, TabelaPreco } from '../../db/tipos'
import { estadoDoAdicionar, termoDaBuscaDaFicha } from '../../produtos/botoesDaFicha'
import JanelaCompartilhar from './JanelaCompartilhar'
import JanelaAdicionarAoPedido from './JanelaAdicionarAoPedido'
import type { HistoricoDoProduto } from '../../produtos/historicoDoProduto'
import { tituloDaFicha } from '../../produtos/tituloDoProduto'
import AbaComercialVendo from './AbaComercialVendo'
import AbaFiscalVendo from './AbaFiscalVendo'
import AbaHistoricoVendo from './AbaHistoricoVendo'
import { IconeCaixa, IconeCarrinho, IconeCompartilhar, IconeDocumento, IconeGrafico, IconeLapis, IconeRelogio } from './IconesDaFicha'

export type AbaDaFicha = 'comercial' | 'fiscal' | 'historico'

export interface PropsFicha {
  produto: Produto
  representada: Representada | undefined
  aba: AbaDaFicha
  aoTrocarAba: (a: AbaDaFicha) => void
  aoEditar: () => void
  aoAlternarAtivo: () => void
  tabelasComPreco: TabelaPreco[]
  tabelaEfetiva: string
  aoEscolherTabela: (id: string) => void
  precoAtual: Preco | undefined
  precosDoProduto: Preco[]
  tabelas: TabelaPreco[]
  historico: HistoricoDoProduto | null | undefined
}

const ABAS: ReadonlyArray<readonly [AbaDaFicha, string, () => React.ReactElement]> = [
  ['comercial', 'Dados Comerciais', () => <IconeDocumento tamanho={18} />],
  ['fiscal', 'Dados Fiscais', () => <IconeGrafico tamanho={18} />],
  ['historico', 'Histórico', () => <IconeRelogio tamanho={18} />],
]

// O modo de VER da ficha do produto (spec 2026-09-23, prints dela). Quem manda
// no estado (aba, modo, tabela escolhida) continua sendo o FormProduto.
export default function FichaProdutoVendo(props: PropsFicha) {
  const { produto, aba } = props
  // Titulo LIMPO so aqui -- ha guarda de contrato prendendo isso.
  const titulo = tituloDaFicha(produto.descricao)
  const [janela, setJanela] = useState<'compartilhar' | 'adicionar' | null>(null)
  const adicionar = estadoDoAdicionar(produto, props.precosDoProduto, props.tabelas)
  const tabelaNome = props.tabelas.find((t) => t.id === props.tabelaEfetiva)?.nome ?? null
  return (
    <section className="p-4 pb-28 space-y-4">
      <div className="flex gap-3">
        <div className="w-24 h-24 shrink-0 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center overflow-hidden">
          {produto.foto_url ? <img src={produto.foto_url} alt="" className="w-full h-full object-cover" /> : <IconeCaixa tamanho={36} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <button
              type="button" onClick={props.aoAlternarAtivo}
              className={`rounded-full px-3 py-1 text-xs font-bold text-white shadow-[0_2px_4px_rgba(0,0,0,.25),inset_0_1px_0_rgba(255,255,255,.4),inset_0_-1px_2px_rgba(0,0,0,.15)] ${
                produto.ativo ? 'bg-gradient-to-b from-emerald-400 to-emerald-600' : 'bg-gradient-to-b from-red-400 to-red-600'
              }`}
            >
              {produto.ativo ? 'Ativo' : 'Inativo'}
            </button>
            <button
              type="button" onClick={props.aoEditar} aria-label="Editar"
              className="w-9 h-9 flex items-center justify-center border border-teal-700 text-teal-700 rounded-lg shrink-0"
            >
              <IconeLapis tamanho={16} />
            </button>
          </div>
          <h2 className="mt-2 text-lg font-bold leading-tight text-slate-900">{titulo}</h2>
          {produto.codigo !== '' && <p className="text-sm font-bold text-slate-700">{produto.codigo}</p>}
        </div>
      </div>

      {/* O icone vai num span aria-hidden: o nome acessivel do botao tem de
          continuar exatamente "Dados Fiscais" -- o E2E clica por ele. */}
      {/* Icone EM CIMA do texto: a 360px cada aba tem ~105px, e "Dados
          Comerciais" com o icone ao lado ficava espremido na borda (foto dela,
          23/09 20h58). A ativa e uma pilula verde com a barra embaixo, como no print. */}
      <div className="flex gap-1 border-b-2 border-slate-200">
        {ABAS.map(([valor, rotulo, icone]) => (
          <button
            key={valor} type="button" onClick={() => props.aoTrocarAba(valor)}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 -mb-0.5 text-xs font-semibold rounded-t-xl border-b-2 ${
              aba === valor ? 'bg-teal-700 text-white border-teal-900 shadow-sm' : 'text-slate-600 border-transparent'
            }`}
          >
            <span aria-hidden="true">{icone()}</span>{rotulo}
          </button>
        ))}
      </div>

      {aba === 'comercial' && (
        <AbaComercialVendo
          produto={produto} representada={props.representada}
          tabelasComPreco={props.tabelasComPreco} tabelaEfetiva={props.tabelaEfetiva}
          aoEscolherTabela={props.aoEscolherTabela} precoAtual={props.precoAtual}
        />
      )}
      {aba === 'fiscal' && <AbaFiscalVendo produto={produto} />}
      {aba === 'historico' && <AbaHistoricoVendo produtoId={produto.id} historico={props.historico} />}

      {/* Colado em cima da barra de navegacao do Layout (py-3 + text-sm + borda
          = 45px). O pb-28 da section impede o ultimo card de ficar atras dele. */}
      <div className="fixed inset-x-0 bottom-[45px] z-30 bg-white/95 border-t border-slate-200 px-4 py-2">
        {!adicionar.pode && <p className="text-xs text-slate-500 mb-1">{adicionar.motivo}</p>}
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setJanela('compartilhar')}
            className="flex items-center justify-center gap-1.5 rounded-lg border border-teal-700 text-teal-700 py-2.5 text-sm font-semibold whitespace-nowrap">
            <span aria-hidden="true"><IconeCompartilhar tamanho={18} /></span>Compartilhar
          </button>
          <button type="button" disabled={!adicionar.pode} onClick={() => setJanela('adicionar')}
            className="flex items-center justify-center gap-1.5 rounded-lg bg-teal-700 text-white py-2.5 text-sm font-semibold whitespace-nowrap disabled:opacity-40">
            <span aria-hidden="true"><IconeCarrinho tamanho={18} /></span>Adicionar ao pedido
          </button>
        </div>
      </div>
      {janela === 'compartilhar' && (
        <JanelaCompartilhar produto={produto} tabelaNome={tabelaNome} precoAtual={props.precoAtual} aoFechar={() => setJanela(null)} />
      )}
      {janela === 'adicionar' && (
        <JanelaAdicionarAoPedido termo={termoDaBuscaDaFicha(produto)} aoFechar={() => setJanela(null)} />
      )}
    </section>
  )
}
